import type { ControlResultFieldName } from "api/generated/model";

import { ControlActivityListColumnId } from "./types";

/**
 * Non-displayable id fields that are always exported (mirrors the legacy
 * `ID_FIELD_NAMES`), prepended before the visible-column fields.
 */
export const ID_FIELD_NAMES: ControlResultFieldName[] = [
    "Id",
    "ControlId",
    "ControlProgramId",
];

/**
 * `null` columns are intentionally not exported:
 *  - `tags` expands server-side into one `Metadata: <category>` column per tag
 *    category, so the dynamic `category_*` columns are display-only.
 *  - `responsibleOrReviewer` / `classificationClass` / `archived` / `health` /
 *    `controlProgramStatus` have no corresponding export field.
 */
export const EXPORT_FIELD_BY_COLUMN: Record<
    ControlActivityListColumnId,
    ControlResultFieldName | null
> = {
    controlProgramId: "ControlProgram", // renders the control program NAME, not the ID
    controlProgramStatus: null,
    controlId: "Title", // renders the control result TITLE, not the ID
    entity: "Entity",
    description: "Description",
    period: "CoveredPeriod",
    tags: "Tags",
    created: "Created",
    completionDueDate: "CompletionDueDate",
    firstReviewerDueDate: "FirstReviewerDueDate",
    secondReviewerDueDate: "SecondReviewerDueDate",
    dueDate: "DueDate",
    timeToDueDate: null,
    completedDate: "CompletedDate",
    firstReviewedDate: "FirstReviewedDate",
    secondReviewedDate: "SecondReviewedDate",
    completedAndReviewedDate: "CompletedAndReviewedDate",
    responsible: "User",
    claimant: "ClaimantUser",
    performer: "Performer",
    reviewedDate: "ReviewResultReviewedDate",
    reviewer: "ReviewResultUser",
    reviewPerformer: "ReviewResultPerformer",
    secondReviewer: "SecondReviewResultUser",
    secondReviewPerformer: "SecondReviewResultPerformer",
    responsibleOrReviewer: null,
    workflowStatus: "WorkflowStatus",
    health: null, // display-only column, not exported
    frictionType: null,
    classificationClass: null,
    archived: null,
};

/**
 * Builds the ordered, de-duplicated list of export field names for the given
 * visible column ids. The id fields are always prepended; UI-only columns
 * (mapped to `null`) and unknown ids (e.g. dynamic `category_*` columns) are
 * skipped.
 *
 * The backend writes the columns in this order, expanding `Tags` in place.
 */
export function buildExportFieldNames(
    visibleColumnIds: string[]
): ControlResultFieldName[] {
    const mappedFields = visibleColumnIds
        .map((id) => EXPORT_FIELD_BY_COLUMN[id as ControlActivityListColumnId])
        .filter(
            (fieldName): fieldName is ControlResultFieldName =>
                fieldName !== null && fieldName !== undefined
        );
    return Array.from(new Set([...ID_FIELD_NAMES, ...mappedFields]));
}
